import React from 'react';
import PropTypes from 'prop-types';
import Button from '../common/Button';
import { convertRandsToUnits, formatUnits, hasValidRates, isFlatRate } from '../../config/siteConfig';
import { useSiteRates } from '../../context/SiteRatesContext';

/**
 * A card component displaying the user's prepaid account balance information.
 * When the resident's site has a runtime kWh rate config (via SiteRatesContext),
 * displays Units (kWh) as primary with Rands as secondary, plus rate information.
 * Sites without a configured rate show Rands only.
 * 
 * @param {Object} props - Component props
 * @param {number} props.balance - Current account balance in Rands
 * @param {boolean} props.showBuyButton - Whether to show the "Buy Electricity" button
 * @param {string} props.buttonText - Text to display on the buy button
 * @param {string} props.siteName - The site name (used for site-specific display)
 */
const PrepaidAccountCard = ({ 
  balance, 
  showBuyButton = true, 
  buttonText = "Buy Electricity",
  siteName = null 
}) => {
  const hasError = balance === null || typeof balance === 'undefined';
  const { getSiteConfig } = useSiteRates();
  const siteConfig = getSiteConfig(siteName);
  const isKwh = hasValidRates(siteConfig);

  const units = isKwh ? convertRandsToUnits(balance, siteConfig.currentRates.peak) : 0;

  return (
    <div className="card bg-beige-40 shadow-md rounded-lg p-6">
      <h2 className="hooray-dark-text text-sm font-semibold uppercase mb-2">Account Balance</h2>

      {hasError ? (
        <div className="bg-red-50 p-4 rounded-md mt-1"> 
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Failed to load balance data</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>Please try refreshing the page or contact support.</p>
              </div>
            </div>
          </div>
        </div>
      ) : isKwh ? (
        <>
          <div className="flex items-baseline">
            <span className="text-3xl font-bold text-hooray-red">
              {formatUnits(units)}
            </span>
            <span className="text-lg font-semibold text-hooray-red ml-1">Units (kWh)</span>
          </div>
          <div className="text-sm text-gray-600 mt-1">
            R {(typeof balance === 'number' ? balance.toFixed(2) : '0.00')}
          </div>
          
          <div className="mt-4 pt-3 border-t border-gray-200">
            <h3 className="text-xs font-semibold uppercase text-gray-500 mb-2">
              {isFlatRate(siteConfig.currentRates) ? 'Rate' : 'Rates'}
            </h3>
            <div className="space-y-1 text-sm">
              {isFlatRate(siteConfig.currentRates) ? (
                <div className="flex justify-between">
                  <span className="text-gray-600">All day (flat rate):</span>
                  <span className="font-medium">R{siteConfig.currentRates.peak.toFixed(2)}/kWh</span>
                </div>
              ) : (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Peak (16:00-08:00):</span>
                    <span className="font-medium">R{siteConfig.currentRates.peak.toFixed(2)}/kWh</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Off-Peak (08:00-16:00):</span>
                    <span className="font-medium">R{siteConfig.currentRates.offPeak.toFixed(2)}/kWh</span>
                  </div>
                </>
              )}
            </div>
          </div>
          
          {showBuyButton && (
            <Button 
              to="/billing" 
              variant="cta" 
              className="w-full mt-4"
            >
              {buttonText}
            </Button>
          )}
        </>
      ) : (
        <>
          <div className="flex items-baseline">
            <span className="text-3xl font-bold text-hooray-red">
              R {(typeof balance === 'number' ? balance.toFixed(2) : '0.00')}
            </span>
          </div>
          <div className="text-sm text-gray-500 mt-1">
            Available credit
          </div>
          {showBuyButton && (
            <Button 
              to="/billing" 
              variant="cta" 
              className="w-full mt-4"
            >
              {buttonText}
            </Button>
          )}
        </>
      )}
    </div>
  );
};

PrepaidAccountCard.propTypes = {
  balance: PropTypes.number,
  showBuyButton: PropTypes.bool,
  buttonText: PropTypes.string,
  siteName: PropTypes.string
};

export default PrepaidAccountCard; 